/**
 * Phase 6.2 — Portal comment composer + list.
 *
 * Plain-text only. The frontend never renders user-submitted HTML or
 * markdown — comment bodies render inside `<p>` text nodes which React
 * automatically escapes.
 *
 * Hidden when `portal_comments_enabled` is OFF (api returns 404 on the
 * GET call → `available=false`).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { MessageSquare, Send, X, Clock, CheckCircle2, XCircle } from 'lucide-react';
import portalApi from '@/lib/portalApi';

const MAX_BODY = 2000;

const STATUS_META = {
  pending:   { label: 'Pending review', cls: 'bg-amber-100 text-amber-900 border-amber-200',   Icon: Clock },
  approved:  { label: 'Approved',       cls: 'bg-emerald-100 text-emerald-900 border-emerald-200', Icon: CheckCircle2 },
  rejected:  { label: 'Not approved',   cls: 'bg-rose-100 text-rose-900 border-rose-200',       Icon: XCircle },
  withdrawn: { label: 'Withdrawn',      cls: 'bg-muted text-muted-foreground border-border',    Icon: X },
};

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function PortalCommentList({ targetKind, targetId, refreshToken = 0 }) {
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  const fetchItems = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await portalApi.get('/portal/comments', {
        params: { target_kind: targetKind, target_id: targetId },
      });
      setItems(r.data?.data?.items || []);
      setAvailable(true);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 404) setAvailable(false);
      else setError(e?.response?.data?.detail || e.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [targetKind, targetId]);

  useEffect(() => { fetchItems(); }, [fetchItems, refreshToken]);

  const withdraw = useCallback(async (id) => {
    try {
      await portalApi.post(`/portal/comments/${id}/withdraw`);
      await fetchItems();
    } catch (e) {
      setError(e?.response?.data?.detail?.error?.message || 'Could not withdraw the comment');
    }
  }, [fetchItems]);

  if (!available) return null;
  if (loading && items.length === 0) {
    return <p className="text-xs text-muted-foreground" data-testid="portal-comments-loading">Loading comments…</p>;
  }
  if (error) {
    return <p className="text-xs text-rose-600" data-testid="portal-comments-error">{error}</p>;
  }
  if (items.length === 0) {
    return null;  // Composer below will be the only affordance.
  }

  return (
    <ul className="space-y-2 mt-3" data-testid={`portal-comments-list-${targetKind}-${targetId}`}>
      {items.map((c) => {
        const meta = STATUS_META[c.status] || STATUS_META.pending;
        return (
          <li
            key={c.id}
            className="rounded-md border bg-card/60 p-2.5"
            data-testid={`portal-comment-${c.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <MessageSquare className="h-3 w-3" />
                <span>{c.is_own ? `You · ${c.author_label}` : c.author_label}</span>
                <span>·</span>
                <span>{relativeTime(c.created_at)}</span>
              </div>
              <Badge variant="outline" className={`text-[10px] ${meta.cls}`} data-testid={`portal-comment-status-${c.id}`}>
                <meta.Icon className="h-3 w-3 mr-1" /> {meta.label}
              </Badge>
            </div>
            <p className="text-sm mt-1 whitespace-pre-wrap break-words" data-testid={`portal-comment-body-${c.id}`}>
              {/* React auto-escapes — no html / markdown rendered. */}
              {c.body}
            </p>
            {c.is_own && c.status === 'pending' && (
              <div className="mt-2">
                <Button
                  size="sm" variant="outline"
                  onClick={() => withdraw(c.id)}
                  data-testid={`portal-comment-withdraw-${c.id}`}
                >
                  <X className="h-3 w-3 mr-1" /> Withdraw
                </Button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function PortalCommentComposer({ targetKind, targetId, onSubmitted }) {
  const [available, setAvailable] = useState(true);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // Probe availability with a lightweight GET — if 404, the whole composer hides.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await portalApi.get('/portal/comments', { params: { target_kind: targetKind, target_id: targetId } });
        if (!cancelled) setAvailable(true);
      } catch (e) {
        if (!cancelled && e?.response?.status === 404) setAvailable(false);
      }
    })();
    return () => { cancelled = true; };
  }, [targetKind, targetId]);

  const remaining = useMemo(() => MAX_BODY - body.length, [body]);
  const canSubmit = body.trim().length > 0 && remaining >= 0 && !submitting;

  const submit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError(null);
    try {
      await portalApi.post('/portal/comments', {
        target_kind: targetKind,
        target_id: targetId,
        body: body.trim(),
      });
      setBody('');
      setRefreshToken((n) => n + 1);
      if (typeof onSubmitted === 'function') onSubmitted();
    } catch (e) {
      setError(
        e?.response?.data?.detail?.error?.message
        || e?.response?.data?.detail
        || 'Could not submit your comment',
      );
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, body, targetKind, targetId, onSubmitted]);

  if (!available) return null;

  return (
    <Card className="mt-3" data-testid={`portal-comment-composer-${targetKind}-${targetId}`}>
      <CardContent className="p-3 space-y-2">
        <PortalCommentList targetKind={targetKind} targetId={targetId} refreshToken={refreshToken} />
        <div className="flex items-start gap-2 pt-1">
          <MessageSquare className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
          <div className="flex-1 space-y-1">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY + 100))}
              placeholder="Add a comment for your care team…"
              rows={3}
              maxLength={MAX_BODY + 100}
              className="w-full text-sm rounded-md border bg-background px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="portal-comment-input"
            />
            <div className="flex items-center justify-between">
              <span
                className={`text-[11px] ${remaining < 0 ? 'text-rose-600 font-medium' : 'text-muted-foreground'}`}
                data-testid="portal-comment-char-counter"
              >
                {remaining < 0
                  ? `${Math.abs(remaining)} over limit`
                  : `${remaining} characters left`}
              </span>
              <Button
                size="sm"
                disabled={!canSubmit}
                onClick={submit}
                data-testid="portal-comment-submit"
              >
                <Send className="h-3 w-3 mr-1" />
                {submitting ? 'Posting…' : 'Post comment'}
              </Button>
            </div>
            {error && (
              <p className="text-xs text-rose-600" data-testid="portal-comment-submit-error">{error}</p>
            )}
            <p className="text-[10px] text-muted-foreground">
              Comments are reviewed by your care team before they appear here.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default PortalCommentComposer;
